import React from 'react';
import { motion } from 'framer-motion';

const PostPaydayLandingPage = ({ goToInput, theme = "mint" }) => {
  // Theme-based button styling
  const buttonStyles = {
    mint: "bg-emerald-500 hover:bg-emerald-600",
    pink: "bg-pink-500 hover:bg-pink-600",
    lavender: "bg-purple-500 hover:bg-purple-600"
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: { duration: 0.3 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  return (
    <motion.div
      className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl p-6 mb-8"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* SEO-optimized heading structure */}
      <h1 className="text-3xl font-bold text-center mb-6 dark:text-white">
        Post-Payday Shopping Spree: Track & Tame Your Paycheck Splurges
      </h1>
      
      {/* Hero section with main CTA */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-xl p-6 mb-8">
        <div className="flex flex-col md:flex-row items-center">
          <div className="mb-6 md:mb-0 md:mr-6 text-center md:text-left">
            <h2 className="text-2xl font-bold mb-3 dark:text-white">Control Your Post-Payday Splurges</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              The first 72 hours after payday account for 60% of monthly impulse purchases. Track your post-payday spending and break the cycle.
            </p>
            <motion.button
              onClick={goToInput}
              className={`px-6 py-3 rounded-xl text-white font-semibold ${buttonStyles[theme]}`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Track Your Payday Purchases
            </motion.button>
          </div>
          <div className="text-8xl flex-shrink-0">💰</div>
        </div>
      </div>
      
      {/* Payday Problem Section */}
      <motion.section 
        className="mb-8"
        variants={itemVariants}
      >
        <h2 className="text-2xl font-bold mb-4 dark:text-white">The Post-Payday Problem</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">💸</span>
              <div>
                <h3 className="font-bold dark:text-white">The Payday High</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  That rush of relief and abundance when your paycheck hits leads to immediate reward shopping.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">📊</span>
              <div>
                <h3 className="font-bold dark:text-white">The 60/40 Split</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Most people spend 60% of their discretionary budget within 3 days of getting paid.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">😔</span>
              <div>
                <h3 className="font-bold dark:text-white">Paycheck-to-Paycheck Cycle</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Early splurges lead to tight budgets later in the month, creating stress and financial anxiety.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">🤔</span>
              <div>
                <h3 className="font-bold dark:text-white">False Abundance</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  The temporary feeling of wealth leads to purchases you'd never make at the end of the pay period.
                </p>
              </div>
            </div>
          </div>
        </div>
      </motion.section>
      
      {/* How Our Tool Helps */}
      <motion.section 
        className="mb-8"
        variants={itemVariants}
      >
        <h2 className="text-2xl font-bold mb-4 dark:text-white">How Our Post-Payday Tracker Helps</h2>
        <div className="space-y-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">1</span>
              Payday Alert System
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Get notified when you're in the high-risk spending period and see your real-time budget.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">2</span>
              First 72-Hours Focus
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Special tracking for the critical first three days after payment when impulse spending peaks.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">3</span>
              Payday Spending Distribution
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              See exactly how much of your monthly budget is consumed right after payday versus later periods.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">4</span>
              Cooling-Off Challenges
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Post-payday specific challenges to delay gratification and spread spending throughout the month.
            </p>
          </div>
        </div>
      </motion.section>
      
      {/* Payday Spending Types */}
      <motion.section 
        className="mb-8"
        variants={itemVariants}
      >
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Discover Your Payday Spending Type</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">🛍️</span>
              <h3 className="font-bold mt-2 dark:text-white">The Celebrator</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              You see payday as a reward, treating yourself to immediate indulgences as a celebration of your hard work.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">🧠</span>
              <h3 className="font-bold mt-2 dark:text-white">The Relief Spender</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              The stress relief of money arriving triggers emotional spending as your financial anxiety temporarily lifts.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">📱</span>
              <h3 className="font-bold mt-2 dark:text-white">The Cart Clearer</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              You've been filling online shopping carts all month, waiting for payday to hit "checkout" on everything at once.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">⏰</span>
              <h3 className="font-bold mt-2 dark:text-white">The Scheduler</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              You deliberately plan major purchases around payday, which can be strategic or lead to overspending.
            </p>
          </div>
        </div>
      </motion.section>
      
      {/* Payday Stats */}
      <motion.section 
        className="mb-8 bg-gray-50 dark:bg-gray-700 rounded-xl p-5"
        variants={itemVariants}
      >
        <h2 className="text-xl font-bold mb-4 dark:text-white">Post-Payday Spending Reality Check</h2>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold dark:text-white">73%</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">of people spend more in the week after payday</p>
          </div>
          
          <div>
            <div className="text-2xl font-bold dark:text-white">3 Days</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">critical window for impulse purchasing</p>
          </div>
          
          <div>
            <div className="text-2xl font-bold dark:text-white">40%</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">higher guilt scores on post-payday purchases</p>
          </div>
          
          <div>
            <div className="text-2xl font-bold dark:text-white">2.5x</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">more non-essential purchases after getting paid</p>
          </div>
        </div>
      </motion.section>
      
      {/* Budget Distribution Visualization */}
      <motion.section 
        className="mb-8"
        variants={itemVariants}
      >
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Typical Monthly Spending Pattern</h2>
        
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="font-medium dark:text-white">Days 1-3 (Post-Payday)</span>
                <span className="font-medium dark:text-white">60%</span>
              </div>
              <div className="h-4 w-full bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div className="h-full bg-red-500 rounded-full" style={{ width: "60%" }}></div>
              </div>
            </div>
            
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="font-medium dark:text-white">Days 4-14</span>
                <span className="font-medium dark:text-white">25%</span>
              </div>
              <div className="h-4 w-full bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div className="h-full bg-yellow-500 rounded-full" style={{ width: "25%" }}></div>
              </div>
            </div>
            
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="font-medium dark:text-white">Days 15-30</span>
                <span className="font-medium dark:text-white">15%</span>
              </div>
              <div className="h-4 w-full bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div className="h-full bg-green-500 rounded-full" style={{ width: "15%" }}></div>
              </div>
            </div>
          </div>
          
          <div className="text-center text-sm text-gray-500 dark:text-gray-400 mt-4">
            Discretionary spending distribution throughout a typical monthly pay cycle
          </div>
        </div>
      </motion.section>
      
      {/* Testimonials section */}
      <motion.section 
        className="mb-8"
        variants={itemVariants}
      >
        <h2 className="text-2xl font-bold mb-4 dark:text-white">How Others Tamed Their Payday Splurges</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex flex-col h-full">
              <p className="text-gray-600 dark:text-gray-300 italic mb-4">
                "I used to blow through 70% of my discretionary budget within 48 hours of getting paid. The Post-Payday tracker helped me spread my spending evenly through the month."
              </p>
              <div className="mt-auto">
                <p className="font-semibold dark:text-white">Marcus T.</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Former Cart Clearer</p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex flex-col h-full">
              <p className="text-gray-600 dark:text-gray-300 italic mb-4">
                "The 72-hour spending challenges helped me break my 'treat yourself' payday habit. Now I allocate my paycheck strategically instead of emotionally."
              </p>
              <div className="mt-auto">
                <p className="font-semibold dark:text-white">Aisha K.</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Reformed Celebrator</p>
              </div>
            </div>
          </div>
        </div>
      </motion.section>
      
      {/* FAQ Section - important for SEO */}
      <motion.section 
        className="mb-8"
        variants={itemVariants}
      >
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Common Questions About Post-Payday Spending</h2>
        <div className="space-y-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">Why do I spend so much right after getting paid?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              The sudden influx of money creates a feeling of abundance that triggers reward-seeking behavior. This "wealth effect" makes you temporarily feel richer than you are, leading to increased spending and reduced financial caution.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">How can I stop blowing my paycheck in the first week?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Our Post-Payday Tracker helps you implement the 72-hour rule (waiting three days before major purchases), create a payday spending budget, and automate transfers to savings right when you get paid. Tracking specific post-payday guilt triggers also helps break the cycle.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">What's a healthy post-payday spending percentage?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Financial advisors recommend limiting the first week's discretionary spending to 25-30% of your monthly fun money, rather than the typical 60%. Our tracker helps you gradually reduce your post-payday spending percentage to achieve a more balanced monthly cash flow.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">How does delayed gratification improve financial health?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Spacing out your discretionary spending throughout the month improves financial decision-making, reduces paycheck-to-paycheck stress, prevents regret purchases, and helps develop long-term wealth-building habits. Our Post-Payday Tracker gamifies this process with challenges and rewards.
            </p>
          </div>
        </div>
      </motion.section>
      
      {/* CTA section */}
      <motion.section 
        className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/30 dark:to-purple-900/30 rounded-xl p-6 text-center"
        variants={itemVariants}
      >
        <h2 className="text-2xl font-bold mb-3 dark:text-white">Ready to Balance Your Paycheck Spending?</h2>
        <p className="text-gray-700 dark:text-gray-300 mb-4">
          Start tracking your post-payday purchases and transform your whole month's financial wellness.
        </p>
        <motion.button
          onClick={goToInput}
          className={`px-8 py-3 rounded-xl text-white font-semibold ${buttonStyles[theme]}`}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Track Your Payday Spending Now
        </motion.button>
      </motion.section>
      
      {/* Rich SEO footer content */}
      <footer className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          <h2 className="text-lg font-semibold mb-2 text-gray-800 dark:text-gray-200">About Post-Payday Spending</h2>
          <p className="mb-3">
            Post-payday spending spikes are a common financial behavior where people spend significantly more money in the days immediately following receipt of their paycheck. This phenomenon, driven by a temporary feeling of financial abundance, often leads to budget imbalances throughout the pay period.
          </p>
          <p className="mb-3">
            Our Post-Payday Tracker helps you understand and address these spending patterns by monitoring your first 72 hours after payment, identifying emotional triggers, and providing strategies to distribute your discretionary spending more evenly throughout the month.
          </p>
          <p>
            Whether you're trying to break the paycheck-to-paycheck cycle, reduce financial stress in the latter part of the month, or build more consistent saving habits, our free tool helps you develop a healthier relationship with your regular income.
          </p>
        </div>
      </footer>
    </motion.div>
  );
};

export default PostPaydayLandingPage;
